/**
 * 강사 정착 퍼널 — T1·T2.
 *
 * 기획서: `DAILY_OPS_BRIEFING_COACH_ONBOARDING.md`
 *
 * ─────────────────────────────────────────────────────────────────
 * 왜 이게 콜드스타트의 진짜 승부처인가
 * ─────────────────────────────────────────────────────────────────
 * 데일리 브리핑은 데이터를 먹고 사는데, **그 데이터를 만드는 사람은 강사**입니다.
 *
 *   회원 연결 → 수강권 등록 → 스케줄 등록 → 수업 진행 → 일지 작성
 *                        ↑ 여기서 다 생성됩니다
 *
 * **강사 한 명이 라포를 안 쓰면 그 담당 회원 전원이 분석에서 통째로 사라집니다.**
 *
 * 그리고 이건 분석 문제가 아니라 돈 문제입니다:
 *   · 수강권 미등록 → **매출이 안 잡힌다.** 정산도 틀린다
 *   · 스케줄 미등록 → 수업 횟수·잔여 회차가 안 맞는다
 *   · 일지 미작성   → 회원이 성장을 못 느끼고 재등록률이 떨어진다
 *
 * 대표님이 「라포에 매출이 왜 이것밖에 안 잡히지」라고 느끼는 원인이 대부분 여기 있고,
 * **지금은 어느 강사가 안 쓰는지 알 방법이 없습니다.**
 *
 * ─────────────────────────────────────────────────────────────────
 * 🔴 순위표로 만들지 않습니다
 * ─────────────────────────────────────────────────────────────────
 * 기획서 §5 의 경고를 그대로 지킵니다.
 *
 * 문책 도구가 되는 순간 **허위 입력**이 시작됩니다 —
 * 실제 테스트 데이터에 일지 내용이 `"...달리기를 했습니다.\nㅇㅇ"` 로 채워진 게 있습니다.
 *
 * 목적은 **평가가 아니라 지원**입니다. 그래서
 *   · 점수를 화면에 그대로 노출하지 않고 **3단계 상태**로만 보여줍니다
 *   · 정렬은 「가장 도움이 필요한 순」이지 「못하는 순」이 아닙니다
 *   · 같은 강사를 하루에 여러 번 지목하지 않습니다 (호출 쪽에서 제한)
 */

/* ═══════════════════════════════════════════════════════════════════
 * 계약 — 서버가 보내 줄 형태
 * ═══════════════════════════════════════════════════════════════════ */

/*
 * 해금 컨텍스트는 `briefingPhase` 가 정본입니다 — 여기서 다시 정의하지 않습니다.
 * (briefingPhase 는 이 파일을 안 부르므로 순환 참조가 없습니다)
 */
import { UnlockContext } from './briefingPhase';

/**
 * 강사 한 명의 정착 원본.
 *
 * 🔴 **모르는 값은 `null`.** `0` 으로 주지 마세요.
 * 예: 일지 집계 경로가 아직 없으면 `journalWritten: null` 입니다.
 *     0 으로 주면 「하나도 안 썼다」가 되어 멀쩡한 강사가 「도움 필요」로 뜹니다.
 */
export type CoachSettlingRaw = {
    coachId: string;
    name: string;

    /** 강사 앱에 로그인했는가. `Auth(role:'coach').deviceToken` */
    appLoggedIn: boolean | null;
    /**
     * 센터가 강사를 연결했고 강사가 앱에서 수락했는가.
     * `center.relations[].isConnected` — 관리자가 연결하면 false 로 시작한다.
     */
    centerAccepted: boolean | null;

    /** 담당 회원 수. `Rapport.relations[]` */
    memberCount: number | null;
    /** 담당 회원 중 앱 초대를 **보낸** 수. `Invitation` */
    inviteSent: number | null;
    /**
     * 그중 회원이 **수락한** 수. `Invitation.status === 'approve'`
     *
     * 🔴 「초대를 안 보낸 것」과 「보냈는데 회원이 안 받은 것」은 완전히 다른 문제고
     * 처방도 다릅니다 — 앞은 강사 교육, 뒤는 회원 안내입니다.
     */
    inviteAccepted: number | null;

    /** 담당 회원 중 활성 수강권이 있는 수 */
    membershipRegistered: number | null;
    /** 스케줄이 하나라도 잡힌 회원 수 */
    scheduleRegistered: number | null;
    /** 진행 완료(출결 처리된) 수업 수 */
    lessonsDone: number | null;
    /**
     * 그중 일지를 쓴 수업 수.
     *
     * ⚠️ 서버 정본은 **`schedule.postId` 의 유무**입니다.
     *    (여기 `Post.contents.length > 0` 이라고 적혀 있었는데 서버 구현과 달랐습니다 —
     *     본문이 빈 일지도 「썼다」로 셉니다. 프론트가 이 값을 다시 세지 않으므로
     *     판정은 안 바뀌지만, 주석이 틀리면 다음 사람이 다른 기준으로 재계산합니다)
     */
    journalWritten: number | null;

    /** 입사일 (KST). `Coach.createdAt` 또는 초대 승인 시점 */
    joinedOn: string | null;
};

/**
 * `GET .../ops-briefing/coach-settling` 응답 (백엔드 3차 B8·B9).
 *
 * 서버는 **원본 수치만** 내려보냅니다. 「막혔다」의 선(센터 평균 × `RELATIVE_FLOOR`)은
 * 프론트가 냅니다 — 서버가 별도 문턱으로 판정하면 두 화면이 어긋납니다.
 */
export type CoachSettlingPayload = {
    coaches: CoachSettlingRaw[];
    /** 재등록한 회원 수. 못 재면 null */
    reregisteredMembers: number | null;
    /** 활성 수강권 보유 회원 수. 못 재면 null */
    totalMembers: number | null;
    /**
     * 신호 해금 컨텍스트 (B9) — `briefingPhase.UnlockContext` 와 **같은 모양**입니다.
     *
     * ⚠️ **집계에 실패하면 서버가 이 키를 통째로 생략합니다.** null 이 아니라 키가 없습니다.
     *    `coachCount`·`membersWithMembership`·`lessonCount` 가 `number` 라 「못 쟀다」를
     *    null 로 말할 자리가 없고, 0 을 넣으면 「수업 0회 센터」가 되어 열려 있어야 할
     *    신호가 전부 닫히기 때문입니다.
     *    **키가 없으면 화면은 해금 안내를 그리지 않습니다** — 기존 동작 유지입니다.
     */
    unlock?: UnlockContext;
};

/* ═══════════════════════════════════════════════════════════════════
 * 8단계 퍼널
 * ═══════════════════════════════════════════════════════════════════ */

export type SettlingStepKey =
    | 'APP_LOGIN'
    | 'CENTER_ACCEPT'
    | 'MEMBER_LINK'
    | 'INVITE_SENT'
    | 'INVITE_ACCEPTED'
    | 'MEMBERSHIP'
    | 'SCHEDULE'
    | 'JOURNAL';

export type SettlingStatus = 'DONE' | 'PARTIAL' | 'TODO' | 'UNKNOWN';

export type SettlingStep = {
    key: SettlingStepKey;
    label: string;
    status: SettlingStatus;
    /** 「12 / 12」의 앞뒤. 못 재면 둘 다 null */
    done: number | null;
    total: number | null;
    /** 🔴 막혔을 때의 처방. 단계마다 완전히 다르다 */
    remedy: string;
};

/**
 * 🔴 정착 점수 가중 (기획서 §5).
 *
 * **화면에 숫자로 노출하지 않습니다.** 정렬과 3단계 판정에만 씁니다.
 * 수강권이 가장 높은 이유: 매출·정산이 여기 걸려 있습니다.
 */
export const SETTLING_WEIGHT: Record<SettlingStepKey, number> = {
    APP_LOGIN: 20,
    CENTER_ACCEPT: 0, // 로그인과 겹쳐서 별도 가중을 주지 않는다
    MEMBER_LINK: 15,
    /*
     * 🔴 「보냈다」보다 「실제로 연동됐다」가 중요하다 — 대표 결정 2026-08-26 (D8).
     *
     * 앞서는 수락을 0점으로 뒀다. 회원 쪽 문제를 강사 점수에 넣는 게 부당해서였다.
     * 그런데 **회원이 앱을 깔지 않으면 그 회원의 데이터가 아예 안 생긴다** —
     * 식단·컨디션·눈바디가 전부 앱에서 들어온다.
     * 초대를 보내 놓고 끝이면 브리핑에 쓸 것이 없다.
     *
     * 그래서 수락에 10을 주고 발송을 5로 낮췄다. 발송은 수락의 전제일 뿐이다.
     * 일지 15 → 10 은 D6 에서 일지가 「참고」 성격이 된 것과 맞춘 것이다.
     *
     * 🔴 다만 **처방은 바꾸지 않았다.** 여기서 막히면 여전히
     * 「회원에게 직접 안내」다 — 강사를 다그쳐서 풀리는 문제가 아니다.
     */
    INVITE_SENT: 5,
    INVITE_ACCEPTED: 10,
    MEMBERSHIP: 25, // ← 가장 높게. 매출·정산이 여기 걸려 있다
    SCHEDULE: 15,
    JOURNAL: 10,
};

const ratio = (done: number | null, total: number | null): SettlingStatus => {
    if (done == null || total == null) return 'UNKNOWN';
    /*
     * 🔴 **분모가 0이면 「완료」가 아니라 「잴 수 없음」이다.**
     *
     * 여기를 `total === 0 → 'DONE'` 으로 뒀다가 연쇄 버그가 났다:
     *   앱 로그인조차 안 한 강사 → 수강권 0건 → 스케줄 분모 0 → **자동 완료 15점**
     *                                        → 일지 분모 0 → **자동 완료 15점**
     *   그래서 아무것도 안 한 강사가 「진행 중」으로 떴다.
     *
     * 수강권이 없어서 스케줄 대상이 없는 것은 「다 했다」가 아니라
     * **앞 단계가 막혀서 여기까지 오지 못한 것**이다.
     */
    if (total === 0) return 'UNKNOWN';
    if (done >= total) return 'DONE';
    return done > 0 ? 'PARTIAL' : 'TODO';
};

/* ═══════════════════════════════════════════════════════════════════
 * 「막혔다」의 기준 — 센터마다 다르다 (D2, 대표 결정 2026-08-26)
 * ═══════════════════════════════════════════════════════════════════ */

/**
 * 단계별 센터 평균 (0~1). 못 내면 키가 없다.
 *
 * 🔴 **강사별 비율의 평균이 아니라 센터 전체 합계의 비율**이다.
 * 회원 1명 담당 강사의 100% 와 12명 담당 강사의 50% 를 같은 무게로 평균 내면
 * 작은 강사가 센터 기준선을 끌어올린다.
 */
export type CenterAverages = Partial<Record<SettlingStepKey, number>>;

/**
 * 평균을 믿으려면 최소 이만큼의 강사가 있어야 한다.
 * 두 명짜리 센터에서 「평균 대비」는 사실상 「서로 비교」다.
 */
export const MIN_SAMPLE_COACHES = 3;

/**
 * 🔴 센터 평균의 몇 배 아래를 「막혔다」로 볼 것인가.
 *
 * **평균 그 자체를 선으로 쓰면 안 된다** — 정의상 늘 절반이 미달이고,
 * 전원이 90% 인 센터에서도 89% 인 강사가 매일 지목된다.
 * 「우리 센터 기준으로도 눈에 띄게 처진」 사람만 짚기 위해 여유를 둔다.
 *
 * 0.6 으로 뒀다가 올렸다 — 평균 80% 인 센터에서 **50% 인 강사가 안 걸렸다.**
 * 0.7 이면 기획서 예시(일지 8/9 = 89%)는 어떤 센터에서도 안 걸린다:
 * 걸리려면 센터 평균이 127% 여야 한다.
 */
export const RELATIVE_FLOOR = 0.7;

/** 평균을 못 낼 때 쓰는 고정선. 기획서 예시(89% 통과 / 25% 지목) 사이 */
export const FALLBACK_STUCK_RATIO = 0.5;

/**
 * 🔴 이 분모 아래에서는 **비율로 판정하지 않는다** — 대표 결정 2026-08-26.
 *
 * 회원 2명 중 1명 등록(50%)이 「막힘」으로 지목되던 실제 구멍을 막는다.
 * 한 명 남은 것을 매일 부르면 그건 관리가 아니다.
 *
 * ⚠️ **부분 진행(PARTIAL)에만 적용한다.**
 * 0건(TODO)은 분모가 작아도 짚는다 — 회원 2명에게 수강권이 하나도 없으면
 * 그건 「표본이 얇은」 게 아니라 **매출이 0**이다.
 * (개발자 권고는 단계 전체 유보였는데, 이 한 가지만 다르게 뒀습니다.)
 */
export const MIN_JUDGE_DENOM = 4;

/**
 * 🔴 「회원 앱 연동」의 분모 — **여기 한 곳에서만 정한다.**
 *
 * 센터 평균(`SUM_OF`)과 개인 판정(`resolveSettlingSteps`)이 각자 이 식을 적고 있었고,
 * 한쪽은 `inviteSent`, 다른 쪽은 `inviteSent ?? memberCount` 였다.
 * 회원 초대는 **발송 기록이라는 개념 자체가 없어**(회원이 앱에서 초대코드를 입력한다)
 * `inviteSent` 가 사실상 늘 null 이라, 센터 평균에서 이 단계가 **통째로 빠지고** 있었다.
 * 그러면 평균이 안 잡히고 → `stuckLine` 이 고정선으로 떨어져 → 개인만 다른 잣대로 재진다.
 *
 * 두 벌로 적으면 반드시 또 갈린다. 부르는 쪽이 둘이면 식은 하나여야 한다.
 */
const inviteAcceptedDenom = (r: CoachSettlingRaw): number | null =>
    r.inviteSent ?? r.memberCount;

const SUM_OF: Record<string, (r: CoachSettlingRaw) => [number | null, number | null]> = {
    INVITE_SENT: (r) => [r.inviteSent, r.memberCount],
    INVITE_ACCEPTED: (r) => [r.inviteAccepted, inviteAcceptedDenom(r)],
    MEMBERSHIP: (r) => [r.membershipRegistered, r.memberCount],
    SCHEDULE: (r) => [r.scheduleRegistered, r.membershipRegistered],
    JOURNAL: (r) => [r.journalWritten, r.lessonsDone],
};

/**
 * 센터 평균을 낸다.
 *
 * 🔴 **앱에 로그인조차 안 한 강사는 빼고 센다.**
 * 시작도 안 한 사람을 분모에 넣으면 기준선이 내려가서
 * 「우리 센터는 원래 이 정도」가 되어 버린다. 그건 기준이 아니라 체념이다.
 */
export const resolveCenterAverages = (raws: CoachSettlingRaw[]): CenterAverages => {
    const active = raws.filter((r) => r.appLoggedIn !== false);
    if (active.length < MIN_SAMPLE_COACHES) return {};

    const out: CenterAverages = {};
    Object.entries(SUM_OF).forEach(([key, pick]) => {
        let done = 0;
        let total = 0;
        let seen = 0;
        active.forEach((r) => {
            const [d, t] = pick(r);
            if (d == null || t == null || t === 0) return;
            done += d;
            total += t;
            seen += 1;
        });
        if (seen >= MIN_SAMPLE_COACHES && total > 0) out[key as SettlingStepKey] = done / total;
    });
    return out;
};

/**
 * 「눈에 띄게 남았는가」.
 *
 * 🔴 조금 남은 것을 「막혔다」고 부르면 안 된다.
 * 기획서 예시에서 김한솔은 **일지 8/9(89%)인데 「잘 하고 있어요」**다.
 * 반면 성현은 수강권 2/8(25%)이라 「6명 남았어요」로 지목된다.
 *
 * 선은 **센터 평균에서 끌어온다** — 일지를 다 쓰는 센터와
 * 원래 안 쓰는 센터에 같은 잣대를 대면 한쪽은 전원 지목, 한쪽은 아무도 안 걸린다.
 */
export const stuckLine = (key: SettlingStepKey, avg: CenterAverages): number => {
    const a = avg[key];
    if (a == null) return FALLBACK_STUCK_RATIO;
    /*
     * 🔴 상한을 두지 않는다.
     *
     * 처음엔 `Math.min(..., FALLBACK)` 으로 고정선을 상한으로 뒀는데 방향이 반대였다 —
     * 전원이 95% 쓰는 센터에서 선이 50% 로 **내려가서**
     * 그 센터 기준으로 명백히 처진 강사를 놓친다.
     * 여유는 RELATIVE_FLOOR(0.7) 이 이미 준다.
     *
     * 평균이 아주 낮은 센터에서는 선도 같이 내려가 아무도 안 걸리는데,
     * 그건 개별 강사가 아니라 **센터 전체의 문제**라 맞는 동작이다.
     * 그 경우는 D+7 에 열리는 라포 벤치마크가 센터를 통째로 비교해 준다.
     */
    return a * RELATIVE_FLOOR;
};

const isStuck = (s: SettlingStep, avg: CenterAverages): boolean => {
    // 이진 단계(앱 로그인 등)와 0건은 분모와 무관하게 즉시 짚는다
    if (s.status === 'TODO') return true;
    if (s.status !== 'PARTIAL') return false;
    if (s.done == null || !s.total) return true;
    // 🔴 표본이 얇으면 비율을 믿지 않는다 (MIN_JUDGE_DENOM)
    if (s.total < MIN_JUDGE_DENOM) return false;
    return s.done / s.total < stuckLine(s.key, avg);
};

const flag = (v: boolean | null): SettlingStatus =>
    v == null ? 'UNKNOWN' : v ? 'DONE' : 'TODO';

export const resolveSettlingSteps = (r: CoachSettlingRaw): SettlingStep[] => [
    {
        key: 'APP_LOGIN',
        label: '앱 로그인',
        status: flag(r.appLoggedIn),
        done: null,
        total: null,
        remedy: '설치 안내와 계정을 확인해 주세요',
    },
    {
        key: 'CENTER_ACCEPT',
        label: '센터 연결 수락',
        status: flag(r.centerAccepted),
        done: null,
        total: null,
        remedy: '앱에서 센터 연결을 수락해야 합니다',
    },
    {
        key: 'MEMBER_LINK',
        label: '담당 회원 연결',
        status: r.memberCount == null ? 'UNKNOWN' : r.memberCount > 0 ? 'DONE' : 'TODO',
        done: r.memberCount,
        total: null,
        remedy: '회원 연결 방법을 1:1로 알려 주세요',
    },
    {
        key: 'INVITE_SENT',
        label: '회원 앱 초대',
        status: ratio(r.inviteSent, r.memberCount),
        done: r.inviteSent,
        total: r.memberCount,
        // 「몰라서 안 한 것」 — 왜 필요한지를 설명해야 움직인다
        remedy: '회원 앱이 왜 필요한지 설명해 주세요',
    },
    {
        key: 'INVITE_ACCEPTED',
        label: '회원 앱 연동',
        /*
         * 🔴 **앱을 깔고 계정을 연동한 회원 수**다 (서버 확인 2026-08-26).
         *
         *   member.service.js:133 getAppLinkedMap()
         *   → Auth{ role:'member' } 존재 = appAccountStatus 'linked'
         *
         * 「초대 수락」이 아니다. `Invitation` 모델은 센터↔**강사** 초대라
         * 회원과 무관하다 — 제 핸드오프가 여기를 잘못 적어 뒀었다.
         *
         * 분모는 담당 회원 수다. 회원 쪽은 「발송」 기록이 없어
         * (회원이 앱에서 초대코드를 입력하는 구조) `inviteSent` 가 null 일 수 있고,
         * 그때는 담당 회원 수로 잰다.
         *
         * ⚠️ 분모 식을 여기 적지 않는다 — `inviteAcceptedDenom` 하나를 쓴다.
         *    센터 평균과 개인이 다른 분모로 재던 버그가 여기서 났다.
         */
        status: ratio(r.inviteAccepted, inviteAcceptedDenom(r)),
        done: r.inviteAccepted,
        total: inviteAcceptedDenom(r),
        // 🔴 여기서 막힌 건 강사 잘못이 아니다. 회원에게 직접 안내해야 한다.
        remedy: '회원에게 직접 안내가 필요합니다 (강사 문제가 아닙니다)',
    },
    {
        key: 'MEMBERSHIP',
        label: '수강권 등록',
        status: ratio(r.membershipRegistered, r.memberCount),
        done: r.membershipRegistered,
        total: r.memberCount,
        /*
         * 🔴 다른 단계는 「몰라서 못 하는 것」이지만 이건 대개 「안 하는 것」이다.
         * 기존 방식이 편하거나, 매출을 드러내고 싶지 않거나.
         * 그래서 처방이 교육이 아니라 **면담**이다.
         */
        remedy: '교육이 아니라 면담이 필요할 수 있습니다 — 매출과 정산이 여기 걸려 있습니다',
    },
    {
        key: 'SCHEDULE',
        label: '스케줄 등록',
        status: ratio(r.scheduleRegistered, r.membershipRegistered),
        done: r.scheduleRegistered,
        total: r.membershipRegistered,
        remedy: '예약 기능 사용법을 알려 주세요',
    },
    {
        key: 'JOURNAL',
        label: '수업일지',
        status: ratio(r.journalWritten, r.lessonsDone),
        done: r.journalWritten,
        total: r.lessonsDone,
        remedy: '작성 부담을 줄여 주세요 (템플릿·짧은 형식)',
    },
];

/* ═══════════════════════════════════════════════════════════════════
 * 상태 판정
 * ═══════════════════════════════════════════════════════════════════ */

/** 🔴 순위가 아니라 3단계. 기획서 §5 — 「순위로 만들지 않습니다」 */
export type SettlingLevel = 'GOOD' | 'PROGRESS' | 'NEEDS_HELP';

export const LEVEL_META: Record<SettlingLevel, { label: string; tone: string }> = {
    GOOD: { label: '잘 하고 있어요', tone: '#00A793' },
    PROGRESS: { label: '진행 중', tone: '#3D73DD' },
    NEEDS_HELP: { label: '도움 필요', tone: '#E8912A' },
};

export type CoachSettling = {
    coachId: string;
    name: string;
    steps: SettlingStep[];
    level: SettlingLevel;
    /**
     * 지금 막혀 있는 단계. 없으면 null.
     * **가장 앞선 미완료**다 — 앞이 안 되면 뒤는 할 수가 없다.
     */
    blocked: SettlingStep | null;
    /** 정착 점수 0~100. 🔴 화면에 그대로 노출하지 않는다 — 정렬·판정용 */
    score: number;
    /** 잴 수 있는 단계 중 몇 개를 통과했나 */
    doneCount: number;
    countedTotal: number;
    /** 입사 후 며칠째. 모르면 null */
    dayCount: number | null;
    /** 🔴 입사 30일 이내 — 기존 강사와 같은 잣대로 평가하지 않는다 */
    isNewComer: boolean;
};

/** 신규 강사 추적 기간(일). 기획서 §7 */
export const NEWCOMER_DAYS = 30;

/**
 * 🔴 **등급(GOOD/PROGRESS/NEEDS_HELP) 판정에서 빼는 단계.**
 *
 * ─────────────────────────────────────────────────────────────
 * 화면이 자기 처방과 부딪히면 안 된다
 * ─────────────────────────────────────────────────────────────
 * 「회원 앱 연동」의 처방은 **「회원에게 직접 안내가 필요합니다 (강사 문제가 아닙니다)」**
 * 입니다. 회원이 앱을 안 깐 것은 강사가 다그쳐서 풀리는 문제가 아니라는 판단이고,
 * 그래서 처방을 일부러 그렇게 적었습니다.
 *
 * 그런데 이 단계가 등급까지 끌어내리면, 같은 카드에서
 *   「도움 필요」 ← 등급
 *   「강사 문제가 아닙니다」 ← 처방
 * 이 나란히 뜹니다. 원장님은 등급을 먼저 읽고 강사를 부릅니다.
 * 그러면 이 화면은 목적(지원)과 반대로 **문책 도구**가 되고,
 * 문책 도구가 되는 순간 허위 입력이 시작됩니다 (파일 머리말 §5).
 *
 * ─────────────────────────────────────────────────────────────
 * 그래도 점수와 표시에는 남깁니다
 * ─────────────────────────────────────────────────────────────
 * 대표 결정(D8)의 취지는 「보냈다」보다 「실제로 연동됐다」가 중요하다는 것이고,
 * 그건 유효합니다 — 회원이 앱을 안 깔면 식단·컨디션·눈바디가 아예 안 들어와
 * 브리핑에 쓸 것이 없습니다. 그래서
 *   · `score`(정렬·표시)      → 10점 그대로 반영
 *   · 퍼널 단계 표시·`blocked` → 그대로 노출 (막힌 곳을 숨기지 않는다)
 *   · `level`(등급)            → **뺀다**
 * 로 가릅니다. 「챙길 순서」에는 올리되 「강사 등급」은 안 깎는 것입니다.
 *
 * (검토 회신 §3-① 안전장치 · 개발자 회신에서 양쪽 합의)
 */
export const LEVEL_EXCLUDED: SettlingStepKey[] = ['INVITE_ACCEPTED'];

export const resolveCoachSettling = (
    r: CoachSettlingRaw,
    today: Date = new Date(),
    /** 센터 평균. 없으면 고정선으로 판정한다 (D2) */
    avg: CenterAverages = {}
): CoachSettling => {
    const steps = resolveSettlingSteps(r);

    /* 점수 — 잴 수 있는 단계만으로 낸다.
     * 못 재는 단계를 0점으로 넣으면 집계가 안 붙은 센터의 강사가 전부 「도움 필요」가 된다. */
    let got = 0;
    let max = 0;
    /* 등급 판정용 별도 합계 — INVITE_ACCEPTED 를 뺀 것 (아래 LEVEL_EXCLUDED 참고) */
    let levelGot = 0;
    let levelMax = 0;
    steps.forEach((s) => {
        const w = SETTLING_WEIGHT[s.key];
        if (w === 0 || s.status === 'UNKNOWN') return;
        const earned =
            s.status === 'DONE'
                ? w
                : s.status === 'PARTIAL' && s.done != null && s.total
                  ? (w * s.done) / s.total
                  : 0;
        max += w;
        got += earned;
        if (!LEVEL_EXCLUDED.includes(s.key)) {
            levelMax += w;
            levelGot += earned;
        }
    });
    const score = max === 0 ? 0 : Math.round((got / max) * 100);
    /* 🔴 등급은 이 점수로 낸다. 정렬·표시는 위의 `score` 그대로다 */
    const levelScore = levelMax === 0 ? 0 : Math.round((levelGot / levelMax) * 100);

    const counted = steps.filter((s) => s.status !== 'UNKNOWN');
    const doneCount = counted.filter((s) => s.status === 'DONE').length;
    const blocked = steps.find((x) => isStuck(x, avg)) ?? null;

    let dayCount: number | null = null;
    if (r.joinedOn) {
        const t = new Date(`${r.joinedOn}T00:00:00+09:00`).getTime();
        if (Number.isFinite(t)) {
            dayCount = Math.max(1, Math.floor((today.getTime() - t) / 86400000) + 1);
        }
    }

    /* 🔴 잴 수 있는 게 하나도 없으면 판정하지 않는다.
     *    집계가 아직 안 붙었을 뿐인데 「도움 필요」로 낙인찍으면 안 된다.
     *
     * 🔴 `score` 가 아니라 `levelScore` 를 본다 — 「회원 앱 연동」은 등급에서 뺀다
     *    (LEVEL_EXCLUDED). 그 단계만 남았을 때 levelMax 가 0 이 되는 경우도
     *    「못 잰다」와 같게 다뤄 PROGRESS 로 둔다. */
    const level: SettlingLevel =
        levelMax === 0
            ? 'PROGRESS'
            : levelScore >= 80
              ? 'GOOD'
              : levelScore >= 40
                ? 'PROGRESS'
                : 'NEEDS_HELP';

    return {
        coachId: r.coachId,
        name: r.name,
        steps,
        level,
        blocked,
        score,
        doneCount,
        countedTotal: counted.length,
        dayCount,
        isNewComer: dayCount != null && dayCount <= NEWCOMER_DAYS,
    };
};

/**
 * 오늘 챙길 강사 — **가장 도움이 필요한 순**.
 *
 * 🔴 「못하는 순」이 아닙니다. 정렬 기준이 곧 화면의 메시지가 됩니다.
 * 앱 로그인조차 안 한 강사가 일지를 덜 쓴 강사보다 급합니다 —
 * 담당 회원 전원의 데이터가 통째로 안 쌓이고 있기 때문입니다.
 */
export const sortByNeed = (list: CoachSettling[]): CoachSettling[] =>
    [...list].sort((a, b) => {
        const stuck = (x: CoachSettling) =>
            x.blocked ? x.steps.findIndex((s) => s.key === x.blocked!.key) : 99;
        const sa = a.blocked ? stuck(a) : 99;
        const sb = b.blocked ? stuck(b) : 99;
        if (sa !== sb) return sa - sb; // 앞 단계에서 막힌 사람이 먼저
        return a.score - b.score;
    });

/* ═══════════════════════════════════════════════════════════════════
 * 🔴 커버리지 — T4
 * ═══════════════════════════════════════════════════════════════════ */

export type Coverage = {
    /** 분석에 들어간 회원 수 */
    covered: number;
    /** 센터 전체 회원 수 */
    total: number;
    pct: number;
    /** 데이터가 없는 강사 이름들 */
    missingCoaches: string[];
    /** 그 강사들이 담당한 회원 수 */
    missingMembers: number;
};

/**
 * 이 브리핑이 회원 몇 명을 보고 있는가.
 *
 * 🔴 **이걸 표시하지 않으면 대표님이 「우리 센터는 위험 회원이 없구나」라고 오해합니다.**
 * 실제로는 안 보이는 것뿐입니다. 기획서 §7 이 「반드시 표시해야 한다」고 못박은 항목입니다.
 *
 * 그리고 이건 신규 센터만의 문제가 아닙니다 —
 * 기존 센터도 강사 한 명이 손을 놓으면 그 담당 회원이 통째로 빠집니다.
 */
export const resolveCoverage = (list: CoachSettling[], raws: CoachSettlingRaw[]): Coverage => {
    const byId = new Map(raws.map((r) => [r.coachId, r]));
    let covered = 0;
    let total = 0;
    const missingCoaches: string[] = [];
    let missingMembers = 0;

    list.forEach((c) => {
        const raw = byId.get(c.coachId);
        const members = raw?.memberCount ?? 0;
        total += members;

        /*
         * 🔴 대표 결정(2026-08-26) — **등록된 회원만 커버로 센다.**
         *
         * 앞서는 강사 단위로 「수강권 0건이면 통째로 제외」했는데,
         * 그러면 12명 중 2명만 등록한 강사의 **12명 전원이 「보고 있다」로** 세어졌다.
         * 실제로 판정할 수 있는 건 2명뿐이다.
         *
         * 수강권이 없는 회원은 잔여도 만료도 수업도 안 잡혀
         * 단계 판정 자체가 성립하지 않는다 — 그건 「위험이 없는」 게 아니라
         * **안 보이는 것**이고, 그걸 100% 라고 말하면 원장이 오해한다.
         */
        const appDead = raw?.appLoggedIn === false;
        const registered = appDead ? 0 : (raw?.membershipRegistered ?? members);
        const missing = Math.max(0, members - registered);

        covered += registered;
        missingMembers += missing;

        /* 이름은 「거의 안 쌓이는」 강사만 짚는다 — 한두 명 남은 강사까지
         * 매일 이름이 뜨면 관계가 상한다 (기획서 §5 「하루 1회 제한」과 같은 정신) */
        if (members > 0 && (appDead || registered === 0)) missingCoaches.push(c.name);
    });

    return {
        covered,
        total,
        pct: total === 0 ? 100 : Math.round((covered / total) * 100),
        missingCoaches,
        missingMembers,
    };
};
